/**
 * Intake tab — plan §11 item 1 ("Drive watch list (new, approved, ignored,
 * ingested, failed), upload of an email attachment, assignment to study and
 * negotiation with direction, round and date, approve and ignore, unreadable
 * items with reasons"), built by §13.4 WP-31. Replaces the WP-03 placeholder.
 *
 * WP-95 (owner decision, Jordan, 2026-09-17; plan §16.7 WP-95b): the Upload
 * button and its assign-first dialog are gone. A file dropped on this tab is
 * stored on arrival (DropZone, POST /intake/files) and appears at once as an
 * upload-sourced 'new' row; BulkAssignTable offers every such row's fields as
 * proposals from the file name and submits them in bulk (POST
 * /intake/approve-bulk). The status-grouped table below — and the per-row
 * Approve/Ignore dialogs it opens — is UNCHANGED (design skill Rule 0): the
 * drop zone and the bulk table are added above it, not a replacement of it,
 * so a person can still resolve any one row by hand.
 *
 * Refetch strategy (§4.7 contracts.intake.changed): frontend/src/lib/
 * realtimeTransport.ts exposes only named per-surface subscriptions
 * (subscribeAppointments, subscribeIxverifyNotifications*, ...), no generic
 * channel subscribe — so this tab polls every 60 seconds instead of
 * subscribing. [OWNER]: a future WP could add a
 * subscribeContractsIntakeChanges export to realtimeTransport.ts (the
 * contracts:network: channel this WP's watcher already publishes on) so this
 * tab can drop the poll for a push.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Inbox, RefreshCw } from 'lucide-react';
import { PageHeader, EmptyState, ToastProvider, FilterSelect, primaryBtnCls, secondaryBtnCls, outlineBtnCls } from '../../../ui';
import { useContracts } from '../../ContractsProvider';
import { listIntakePage, proposeForIntakeItems } from '../../api/intake';
import type { ApproveResult, BulkApproveResult, FilenameProposal, IntakeItem, IntakePage, IntakeStatus } from '../../api/intake';
import { listStudies } from '../../api/client';
import type { Study } from '../../api/client';
import { IntakeStatusPill } from './pills';
import { ApproveDialog } from './ApproveDialog';
import { IgnoreDialog } from './IgnoreDialog';
import { DropZone } from './DropZone';
import { BulkAssignTable } from './BulkAssignTable';

const POLL_MS = 60_000;

/** plan §11 item 1's own listed order. */
const STATUS_GROUPS: Array<{ status: IntakeStatus; label: string }> = [
  { status: 'new', label: 'New' },
  { status: 'approved', label: 'Approved' },
  { status: 'ingested', label: 'Ingested' },
  { status: 'ignored', label: 'Ignored' },
  { status: 'failed', label: 'Failed' },
];

const STATUS_FILTER_OPTIONS: Array<{ value: IntakeStatus | ''; label: string }> = [
  { value: '', label: 'All' },
  ...STATUS_GROUPS.map((g) => ({ value: g.status, label: g.label })),
];

function formatSize(bytes: number | null): string {
  if (bytes === null || Number.isNaN(bytes)) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

function IntakeTabInner() {
  const { siteId } = useContracts();
  const [items, setItems] = useState<IntakeItem[] | null>(null);
  const [page, setPage] = useState<IntakePage | null>(null);
  const [statusFilter, setStatusFilter] = useState<IntakeStatus | ''>('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [approveTarget, setApproveTarget] = useState<IntakeItem | null>(null);
  const [ignoreTarget, setIgnoreTarget] = useState<IntakeItem | null>(null);
  const [studies, setStudies] = useState<Study[] | null>(null);
  // Upload-sourced rows THIS browser tab has itself stored (via DropZone's
  // onStored), for the bulk table (PART G3/G4) — see the list-sourced merge
  // in bulkRows below for a row that arrived some other way (a reload, a
  // remount). The per-row dialogs (handleApproved, handleIgnored) drop a row
  // here the moment it resolves, since the per-row table below takes over
  // showing it. The bulk table's own submit does NOT drop a row here (WP-95b
  // fix, blocking defect 1): PART G3 requires an approved bulk row to keep
  // showing its own outcome, so it stays — BulkAssignTable just deselects it.
  const [uploadedRows, setUploadedRows] = useState<Map<string, { item: IntakeItem; proposal: FilenameProposal }>>(new Map());
  // WP-95b fix (blocking defect 4): proposals fetched for an upload-sourced
  // 'new' row this tab learned about from the list rather than from DropZone
  // (a reload, or a remount) — keyed by intakeItemId, filled in by the
  // backfill effect below. A row this tab stored itself never needs this:
  // its proposal already came back on POST /intake/files and lives in
  // uploadedRows.
  const [backfilledProposals, setBackfilledProposals] = useState<Map<string, FilenameProposal>>(new Map());
  // Ids already asked of POST /intake/proposals, so the backfill effect below
  // (which reruns on every bulkRows/uploadedRows change) never asks twice.
  const requestedProposalIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    listStudies()
      .then(setStudies)
      .catch(() => setStudies([]));
  }, []);

  // page 0 for the current status filter. 'replace' is a fresh load (mount, or
  // the filter changed); 'merge' (the poll, and the refresh button) keeps
  // whatever the person has loaded past page 0 via Load more and only updates
  // the first PAGE_SIZE rows, so a three-page scroll is never reset out from
  // under them (PART G4).
  const fetchPage0 = useCallback(
    (mode: 'replace' | 'merge') => {
      if (!siteId) return;
      listIntakePage({ status: statusFilter || undefined })
        .then(({ items: fresh, page: pg }) => {
          setLoadError(null);
          setPage(pg);
          setItems((prev) => {
            if (mode === 'replace' || !prev) return fresh;
            const freshIds = new Set(fresh.map((i) => i.id));
            return [...fresh, ...prev.filter((i) => !freshIds.has(i.id))];
          });
        })
        .catch(() => setLoadError('Could not load the intake list.'));
    },
    [siteId, statusFilter]
  );

  useEffect(() => {
    fetchPage0('replace');
    // fetchPage0 already carries statusFilter/siteId in its own dependency
    // list; re-running this effect whenever fetchPage0's identity changes
    // covers both a fresh mount and a filter change with one code path.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPage0]);

  // Poll every 60s — see this file's header for why (no generic realtime
  // subscribe exists yet). 'merge' mode: see fetchPage0's own comment.
  useEffect(() => {
    if (!siteId) return undefined;
    const id = window.setInterval(() => fetchPage0('merge'), POLL_MS);
    return () => window.clearInterval(id);
  }, [siteId, fetchPage0]);

  function loadMore() {
    if (!siteId || !page || !page.hasMore) return;
    const nextOffset = page.offset + page.limit;
    listIntakePage({ status: statusFilter || undefined, offset: nextOffset })
      .then(({ items: more, page: pg }) => {
        setPage(pg);
        setItems((prev) => [...(prev ?? []), ...more]);
      })
      .catch(() => setLoadError('Could not load more of the intake list.'));
  }

  const grouped = useMemo(() => {
    const byStatus = new Map<IntakeStatus, IntakeItem[]>();
    for (const item of items ?? []) {
      const list = byStatus.get(item.status) ?? [];
      list.push(item);
      byStatus.set(item.status, list);
    }
    return byStatus;
  }, [items]);

  // WP-95b fix (blocking defect 4): one row per upload-sourced 'new' intake
  // row — this session's own drops (uploadedRows) plus any such row that
  // arrived from the list instead (a reload, a remount, or another tab's
  // drop the poll picked up). PART G3/G4 binds this on "every field proposed
  // from POST /intake/proposals", which uploadedRows alone cannot satisfy
  // (it is populated only by DropZone's onStored). A row already known to
  // uploadedRows keeps THAT entry — the one with the real proposal from the
  // drop response and, once bulk-approved, its own result — never the plain
  // list copy.
  const bulkRows = useMemo(() => {
    const merged = new Map<string, { item: IntakeItem; proposal: FilenameProposal | null }>(uploadedRows);
    for (const item of items ?? []) {
      if (item.source === 'upload' && item.status === 'new' && !merged.has(item.id)) {
        merged.set(item.id, { item, proposal: backfilledProposals.get(item.id) ?? null });
      }
    }
    return Array.from(merged.values());
  }, [uploadedRows, items, backfilledProposals]);

  // WP-95b fix (blocking defect 4): backfill proposals for a list-sourced
  // upload row (decision 8: computed on demand, never stored — a rule change
  // takes effect with no backfill script of its own). A row this tab dropped
  // itself is always already in uploadedRows, so it never reaches this call
  // (test-safe: every id PART H2-H7 exercises is seeded through DropZone).
  useEffect(() => {
    const ids = bulkRows
      .filter(({ item }) => !uploadedRows.has(item.id) && !requestedProposalIds.current.has(item.id))
      .map(({ item }) => item.id);
    if (ids.length === 0) return;
    for (const id of ids) requestedProposalIds.current.add(id);
    proposeForIntakeItems(ids)
      .then(({ proposals }) => {
        setBackfilledProposals((prev) => {
          const next = new Map(prev);
          for (const p of proposals ?? []) next.set(p.intakeItemId, p);
          return next;
        });
      })
      .catch(() => {
        // Best-effort: a row that fails to backfill still shows in the bulk
        // table with its cells unproposed (blank, never a guessed value — G8).
      });
  }, [bulkRows, uploadedRows]);

  function replaceItem(updated: IntakeItem) {
    setItems((prev) => (prev ? prev.map((i) => (i.id === updated.id ? updated : i)) : prev));
  }

  function syncUploadedRow(item: IntakeItem) {
    setUploadedRows((prev) => {
      if (!prev.has(item.id) || item.status === 'new') return prev;
      const next = new Map(prev);
      next.delete(item.id);
      return next;
    });
  }

  function handleApproved(result: ApproveResult) {
    replaceItem(result.item);
    syncUploadedRow(result.item);
    setApproveTarget(null);
  }

  function handleIgnored(updated: IntakeItem) {
    replaceItem(updated);
    syncUploadedRow(updated);
    setIgnoreTarget(null);
  }

  function handleBulkApproved(results: BulkApproveResult[]) {
    const okResults = results.filter((r): r is Extract<BulkApproveResult, { ok: true }> => r.ok);
    if (okResults.length === 0) return;
    const byId = new Map(okResults.map((r) => [r.intakeItemId, r.item]));
    setItems((prev) => (prev ? prev.map((i) => byId.get(i.id) ?? i) : prev));
    // WP-95b fix (blocking defect 1): an approved row stays in uploadedRows /
    // bulkRows so BulkAssignTable can render its own Approved pill (PART G3:
    // "each row shows its own outcome") — dropping it here raced the same
    // render that wrote state.result and unmounted the row out from under
    // it. BulkAssignTable.handleApproveSelected deselects a succeeded row
    // instead, so a re-submit sends only the rows still needing one.
  }

  function handleStored(item: IntakeItem, proposal: FilenameProposal) {
    setUploadedRows((prev) => {
      const next = new Map(prev);
      next.set(item.id, { item, proposal });
      return next;
    });
    // Appears at once in the grouped list too (decision: "stored on arrival"),
    // as long as it matches whatever status filter is active right now.
    setItems((prev) => {
      if (!prev) return prev;
      if (statusFilter && statusFilter !== item.status) return prev;
      if (prev.some((i) => i.id === item.id)) return prev;
      return [item, ...prev];
    });
  }

  if (!siteId) {
    return <EmptyState icon={Inbox} title="No site selected" hint="Choose a site to see its intake list." />;
  }

  const filterLabel = STATUS_FILTER_OPTIONS.find((o) => o.value === statusFilter)?.label ?? 'All';

  return (
    <div className="space-y-4">
      <PageHeader
        title="Intake"
        icon={Inbox}
        count={items?.length ?? undefined}
        subtitle="Drop files here to store them. Assign them below, one at a time or in bulk."
        actions={
          <button type="button" onClick={() => fetchPage0('merge')} className={outlineBtnCls()} title="Refresh">
            <RefreshCw className="h-4 w-4" />
          </button>
        }
      />

      <DropZone onStored={handleStored} />

      {bulkRows.length > 0 && <BulkAssignTable rows={bulkRows} studies={studies ?? []} onApproved={handleBulkApproved} />}

      <div className="flex flex-wrap items-center gap-2">
        <FilterSelect
          label="Status"
          value={statusFilter}
          onChange={(v) => setStatusFilter(v as IntakeStatus | '')}
          options={STATUS_FILTER_OPTIONS}
          auto
        />
        <span className="text-xs text-gray-500 dark:text-gray-400">
          Showing {items?.length ?? 0} of the {filterLabel} list
        </span>
      </div>

      {loadError && <p className="text-sm text-red-600 dark:text-red-400">{loadError}</p>}
      {items === null && !loadError && <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>}

      {items !== null && items.length === 0 && (
        <EmptyState
          icon={Inbox}
          title="No files in this list yet"
          hint="Drop files in the window above, or choose them, to store them here. Each one stays in this list until it is assigned and approved."
          variant="card"
        />
      )}

      {items !== null && items.length > 0 && (
        <div className="space-y-6">
          {STATUS_GROUPS.filter((g) => (grouped.get(g.status) ?? []).length > 0).map((group) => (
            <div key={group.status}>
              <h2 className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                {group.label} ({(grouped.get(group.status) ?? []).length})
              </h2>
              <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
                <table className="w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800/50">
                    <tr>
                      {['Name', 'Path', 'Modified', 'Size', 'Status', ''].map((h) => (
                        <th key={h} className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {(grouped.get(group.status) ?? []).map((item) => (
                      <tr key={item.id} data-testid={`intake-row-${item.id}`}>
                        <td className="px-4 py-2 font-medium text-gray-900 dark:text-gray-100">{item.drive_name}</td>
                        <td className="px-4 py-2 text-gray-500 dark:text-gray-400">{item.drive_path ?? '—'}</td>
                        <td className="px-4 py-2 text-gray-700 dark:text-gray-300">{formatDate(item.drive_modified_at)}</td>
                        <td className="px-4 py-2 text-gray-700 dark:text-gray-300">{formatSize(item.drive_size)}</td>
                        <td className="px-4 py-2">
                          <IntakeStatusPill status={item.status} />
                          {item.error && (
                            <div className="mt-1 max-w-xs text-xs text-red-600 dark:text-red-400">{item.error}</div>
                          )}
                        </td>
                        <td className="px-4 py-2 text-right">
                          {item.status === 'new' && (
                            <div className="flex justify-end gap-2">
                              <button
                                type="button"
                                data-testid={`approve-btn-${item.id}`}
                                onClick={() => setApproveTarget(item)}
                                className={primaryBtnCls}
                              >
                                Approve
                              </button>
                              <button
                                type="button"
                                data-testid={`ignore-btn-${item.id}`}
                                onClick={() => setIgnoreTarget(item)}
                                className={secondaryBtnCls}
                              >
                                Ignore
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
          {page?.hasMore && (
            <div>
              <button type="button" data-testid="cta-intake-load-more" onClick={loadMore} className={secondaryBtnCls}>
                Load more
              </button>
            </div>
          )}
        </div>
      )}

      {approveTarget && (
        <ApproveDialog item={approveTarget} onClose={() => setApproveTarget(null)} onApproved={handleApproved} />
      )}
      {ignoreTarget && (
        <IgnoreDialog item={ignoreTarget} onClose={() => setIgnoreTarget(null)} onIgnored={handleIgnored} />
      )}
    </div>
  );
}

export function IntakeTab() {
  return (
    <ToastProvider>
      <IntakeTabInner />
    </ToastProvider>
  );
}

export default IntakeTab;
